import { Search, ArrowRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { useState, useRef, useEffect } from "react";

interface SearchInputProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onSearch: (value: string) => void;
  maxLines?: number; // Maximum number of lines before scrolling
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  defaultValue?: string;
}

export function SearchInput({
  className,
  onSearch,
  maxLines = 5, // Default to 5 lines max before scrolling
  value,
  onChange,
  defaultValue,
  ...props
}: SearchInputProps) {
  const [showArrow, setShowArrow] = useState(Math.random() < 0.5);
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<{ start: number; end: number } | null>(null);

  // Use controlled value if provided, otherwise use internal state
  const currentValue = value !== undefined ? value : internalValue;

  // Calculate line height based on the first line
  const getLineHeight = (): number => {
    const textarea = textareaRef.current;
    if (!textarea) return 20; // Default fallback

    // Temporarily set single line of text to measure
    const originalValue = textarea.value;
    const originalHeight = textarea.style.height;
    textarea.value = "X";
    textarea.style.height = "auto";
    const singleLineHeight = textarea.scrollHeight;

    // Restore original values
    textarea.value = originalValue;
    textarea.style.height = originalHeight;

    return singleLineHeight;
  };

  // Save the current cursor position
  const saveCursorPosition = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      cursorPositionRef.current = {
        start: textarea.selectionStart,
        end: textarea.selectionEnd,
      };
    }
  };

  // Restore the saved cursor position
  const restoreCursorPosition = () => {
    const textarea = textareaRef.current;
    const position = cursorPositionRef.current;
    if (textarea && position) {
      textarea.setSelectionRange(position.start, position.end);
    }
  };

  // Function to adjust the height of textarea with max height limit
  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Save cursor position before adjusting height
      saveCursorPosition();

      const lineHeight = getLineHeight();
      const maxHeight = lineHeight * maxLines;

      // Reset height first to get the correct scrollHeight
      textarea.style.height = "auto";

      // Calculate new height
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${Math.max(newHeight, 56)}px`;

      // Add scrolling if content exceeds max height
      textarea.style.overflowY =
        textarea.scrollHeight > maxHeight ? "auto" : "hidden";

      // Restore cursor position
      setTimeout(restoreCursorPosition, 0);
    }
  };

  // Adjust height on initial render and when value changes
  useEffect(() => {
    adjustHeight();
  }, [currentValue, maxLines]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (onChange) {
      onChange(e);
    } else {
      setInternalValue(newValue);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentValue.trim()) {
      onSearch(currentValue);
    }
  };

  // Handle Enter key to submit form, but allow Shift+Enter for new lines
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (currentValue.trim()) {
        onSearch(currentValue);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className={cn("relative w-full", className)}>
      <div className="relative flex group focus-within:ring-2 focus-within:ring-purple-400/20 rounded-xl">
        <Search className="absolute left-4 top-5 h-5 w-5 text-white/70" />
        <textarea
          ref={textareaRef}
          name="search"
          placeholder="Comment pouvons-nous vous aider ?"
          className="min-h-[56px] max-h-[200px] py-3 resize-none w-full rounded-l-xl border-r-0 border border-white/20 bg-white/20 pl-12 pr-4 text-base text-white placeholder-white/70 backdrop-blur-sm transition-all focus:border-purple-400/50 focus:bg-white/30 focus:outline-none group-focus-within:border-purple-400/50 scrollbar-thin scrollbar-thumb-white/30 scrollbar-track-transparent"
          value={currentValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={saveCursorPosition}
          onKeyUp={saveCursorPosition}
          rows={1}
          {...props}
        />
        <button
          type="submit"
          className="self-stretch px-6 rounded-r-xl border border-l-0 border-white/20 bg-white/20 backdrop-blur-sm transition-all hover:bg-white/30 focus:outline-none group-focus-within:border-purple-400/50"
        >
          {showArrow ? (
            <ArrowRight className="h-5 w-5 text-white/70" />
          ) : (
            <Search className="h-5 w-5 text-white/70" />
          )}
        </button>
      </div>
    </form>
  );
}
